import type { Session as SupaSession, User } from '@supabase/supabase-js';
import type { ProjectMemberRow, Role } from '@treetrack/protocol';
import { isDemo, stopDemo } from './demo';
import { supabase, unwrap } from './supabase';

export interface SessionState {
  user: User | null;
  isAdmin: boolean;
  displayName: string;
  memberships: ProjectMemberRow[];
  /** Demo mode: synthetic project in memory, viewer role, no server session. */
  demo: boolean;
}

let state: SessionState = { user: null, isAdmin: false, displayName: '', memberships: [], demo: false };
const listeners = new Set<(s: SessionState) => void>();

export function getSession(): SessionState {
  return isDemo() ? { ...state, demo: true, displayName: state.user ? state.displayName : 'Demo' } : state;
}

export function onSessionChange(fn: (s: SessionState) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

async function loadFor(session: SupaSession | null): Promise<void> {
  const user = session?.user ?? null;
  if (!supabase || !user) {
    state = { user: null, isAdmin: false, displayName: '', memberships: [], demo: false };
  } else {
    const [profile, memberships] = await Promise.all([
      supabase.from('profiles').select('display_name, is_admin').eq('user_id', user.id).maybeSingle(),
      supabase.from('project_members').select('*').eq('user_id', user.id),
    ]);
    const p = (profile.data ?? null) as { display_name: string; is_admin: boolean } | null;
    state = {
      user,
      isAdmin: p?.is_admin ?? false,
      displayName: p?.display_name ?? user.email?.split('@')[0] ?? '',
      memberships: (memberships.data ?? []) as ProjectMemberRow[],
      demo: false,
    };
  }
  for (const l of listeners) l(getSession());
}

export async function initSession(): Promise<void> {
  if (!supabase) return;
  const { data } = await supabase.auth.getSession();
  await loadFor(data.session);
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED' || event === 'TOKEN_REFRESHED') {
      void loadFor(session);
    }
  });
}

/** The caller's effective role in a project, mirroring project_role() in SQL. */
export function roleFor(projectId: string): Role | null {
  if (isDemo()) return 'viewer';
  if (state.isAdmin) return 'admin';
  return state.memberships.find((m) => m.project_id === projectId)?.role ?? null;
}

/** Leave demo mode and tell the views. */
export function leaveDemo(): void {
  stopDemo();
  for (const l of listeners) l(getSession());
}

export function canManage(projectId: string): boolean {
  const r = roleFor(projectId);
  return r === 'admin' || r === 'lead';
}

export async function sendMagicLink(email: string): Promise<void> {
  if (!supabase) throw new Error('backend not configured');
  unwrap(await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: location.origin } }));
}

export async function signOut(): Promise<void> {
  if (isDemo()) {
    leaveDemo();
    return;
  }
  if (!supabase) return;
  await supabase.auth.signOut();
}
